import React from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import LinearProgress from '@material-ui/core/LinearProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import { reset } from 'redux-form';
import { connect } from 'react-redux';
import ReactFileReader from 'react-file-reader';
import validator from 'validator';
import { reduxForm, Field } from 'redux-form/immutable';
import XLSX from 'xlsx';
import { FormHelperText } from '@material-ui/core';

import FormTextField from 'retired/FormTextField';
import Img from 'retired/Img';
import { required, isEmail } from 'utils/validators';
import { PureRender } from 'utils/component-helpers';
import CheckCircleIcon from 'assets/icons/check-circle.svg';
import CancelButtonIcon from 'assets/icons/cancel-button.svg';
import { formatFullName } from 'utils/formatFullName';

import { RoleSelector } from './RoleSelector';

const requiredValidator = required();
const isEmailValidator = isEmail();

class DeedAttendeeAddList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      addedAttendeesWithId: [],
      addedAttendeesWithEmail: [],
      roles: [],
    };

    this.reset = this.reset.bind(this);
    this.submit = this.submit.bind(this);
    this.toggleAttendee = this.toggleAttendee.bind(this);
    this.clearSearchResults = this.clearSearchResults.bind(this);
    this.handleFiles = this.handleFiles.bind(this);
  }

  submit(data) {
    const user = {
      fullName: {
        first: data.get('firstName'),
        last: data.get('lastName'),
      },
      email: data.get('email'),
      id: data.get('email'),
    };

    this.toggleAttendee(user);
    this.props.resetForm();
  }

  clearSearchResults() {
    this.props.actions.clearAction();
    this.searchInput.value = '';
    this.searchInput.focus();
  }

  add() {
    this.props.onAdd(
      this.props.deed.id,
      this.state.addedAttendeesWithId,
      this.state.addedAttendeesWithEmail,
      this.state.roles
    );
    this.reset();
  }

  toggleAttendee(newAttendee) {
    const { addedAttendeesWithId, addedAttendeesWithEmail } = this.state;
    const isUserInList =
      addedAttendeesWithId.filter((attendee) => attendee.id === newAttendee.id).length > 0;
    const isEmailInList =
      addedAttendeesWithEmail.filter((attendee) => attendee.email === newAttendee.email).length > 0;

    if (newAttendee.email) {
      this.setState((prevState) => ({
        addedAttendeesWithEmail: isEmailInList
          ? prevState.addedAttendeesWithEmail.filter((attendee) => attendee.id !== newAttendee.id)
          : prevState.addedAttendeesWithEmail.concat(newAttendee),
      }));
    } else {
      this.setState((prevState) => ({
        addedAttendeesWithId: isUserInList
          ? prevState.addedAttendeesWithId.filter((attendee) => attendee.id !== newAttendee.id)
          : prevState.addedAttendeesWithId.concat(newAttendee),
      }));
    }
  }

  reset() {
    this.setState({
      addedAttendeesWithId: [],
      addedAttendeesWithEmail: [],
      roles: [],
    });

    this.props.close();
    this.props.actions.clearAction();
  }

  handleFiles(files) {
    const file = files[0];
    const filename = file.name;
    const fileExt = filename.substring(filename.lastIndexOf('.') + 1, filename.length) || filename;

    const getValidEmailIndex = (userRow) => {
      let index = 0;
      userRow.forEach((el, elIndex) => {
        if (validator.isEmail(el.toLowerCase())) {
          index = elIndex;
        }
      });
      return index;
    };

    const reader = new FileReader();
    reader.onload = (e) => {
      let rows = [];
      if (fileExt === 'xlsx' || fileExt === 'xls') {
        let binary = '';
        const bytes = new Uint8Array(e.target.result);
        const length = bytes.byteLength;
        for (let i = 0; i < length; i += 1) {
          binary += String.fromCharCode(bytes[i]);
        }
        // call 'xlsx' to read the file
        const workbook = XLSX.read(binary, {
          type: 'binary',
          cellDates: true,
          cellStyles: true,
        });
        const firstWorksheet = workbook.Sheets[workbook.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(firstWorksheet, { header: 1 });
      } else {
        rows = reader.result
          .split('\n')
          .map((row) => (row.indexOf(';') !== -1 ? row.split(';') : row.split(',')));
      }

      const userRows = rows
        .map((row) => row.map((rowItem) => String(rowItem).replace(/["']/g, '').trim()))
        .filter((row) => {
          // Go through each element in the row and determine if there's a valid email
          let emailExists = false;
          row.forEach((rowItem) => {
            if (validator.isEmail(rowItem.toLowerCase())) {
              emailExists = true;
            }
          });
          return emailExists;
        });

      // Clean up the elements
      userRows.forEach((userRow) => {
        const validEmailIndex = getValidEmailIndex(userRow);

        const newUser = {
          fullName: {},
          email: userRow[validEmailIndex].toLowerCase(),
          id: userRow[validEmailIndex].toLowerCase(),
        };

        let first = '';
        let last = '';
        if (userRow.length === 2) {
          // Email, Full name (in no particular order)
          const splitName = String(userRow[validEmailIndex === 1 ? 0 : 1]).split(' ');
          [first, last] = splitName;
        } else if (userRow.length > 2) {
          // Email, first name, last name (in no particular order)
          // email is at the beginning of the line item,
          // which means the next two elements are the first & last name
          if (validEmailIndex === 0) {
            [first, last] = userRow.slice(1);
            // email is at the end of the line item
            // which means the first two elements are the first & last name
          } else if (validEmailIndex === 2) {
            [first, last] = userRow;
          }
        }
        newUser.fullName = { first, last };

        this.toggleAttendee(newUser);
      });
    };

    if (fileExt === 'xlsx' || fileExt === 'xls') {
      reader.readAsArrayBuffer(file);
    } else if (fileExt === 'csv') {
      reader.readAsText(file);
    }
  }

  render() {
    const {
      handleSubmit,
      visible,
      enableSearch,
      searchLoading,
      results,
      term,
      actions,
      invalid,
      titleText,
      buttonAddText,
      deed,
      allowMultipleRoles,
    } = this.props;
    const { addedAttendeesWithId, addedAttendeesWithEmail } = this.state;

    const renderUser = (user, index) => (
      <li key={index * Math.random()}>
        {formatFullName(user.fullName)}
        <button
          onClick={() => {
            this.toggleAttendee(user);
          }}
          type="button"
        >
          <Img src={CancelButtonIcon} style={{ width: '20px' }} alt="" />
        </button>
      </li>
    );

    const users = addedAttendeesWithId.concat(addedAttendeesWithEmail);

    return (
      <Dialog
        open={visible}
        onClose={this.reset}
        fullWidth
        // @NOTE-DP: otherwise Select would go into infinite re-render. A known MUI issue
        disableEnforceFocus
      >
        <DialogTitle>{titleText}</DialogTitle>
        <DialogContent>
          <ReactFileReader handleFiles={this.handleFiles} fileTypes={['.xlsx', '.xls', '.csv']}>
            <Button style={{ marginBottom: 10, display: 'block' }} type="submit" color="primary">
              Upload
            </Button>
          </ReactFileReader>
          <FormHelperText style={{ marginBottom: 20 }}>
            Upload a .csv, .xls, or .xlsx file containing emails and names
          </FormHelperText>
          {enableSearch ? (
            <div className="search-block">
              {term !== '' && (
                <button
                  type="button"
                  className="clear-search-btn"
                  onClick={() => {
                    this.clearSearchResults();
                  }}
                >
                  <Img src={CancelButtonIcon} style={{ width: '20px' }} alt="Clear search" />
                </button>
              )}
              <TextField
                placeholder={`Search for ${
                  this.props.deed.type === 'BaseEvent' ? 'attendees ' : 'volunteers'
                }`}
                id="search"
                type="text"
                fullWidth
                autoFocus={enableSearch}
                inputProps={{
                  ref: (input) => {
                    this.searchInput = input;
                  },
                }}
                onChange={(e) => {
                  actions.searchAction(e.target.value);
                }}
              />
              {searchLoading && <LinearProgress />}
            </div>
          ) : null}
          {results.size > 0 && (
            <ul className="search-results-list">
              {results.slice(0, 50).map((result) => (
                <li key={result.id}>
                  <div
                    role="button"
                    tabIndex="0"
                    onClick={() => {
                      this.toggleAttendee(result);
                    }}
                    onKeyDown={() => {
                      this.toggleAttendee(result);
                    }}
                    className="search-result"
                  >
                    <span>{formatFullName(result.fullName)}</span>
                    {addedAttendeesWithId.filter((attendee) => attendee.id === result.id).length >
                      0 && (
                      <span className="added">
                        <Img src={CheckCircleIcon} style={{ width: '20px' }} alt="" />
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {results.size === 0 && (
            <div>
              {enableSearch ? <div className="manual-text">Or add them by email</div> : null}
              <form onSubmit={handleSubmit(this.submit)}>
                <Grid container spacing={3}>
                  <Grid item md>
                    <Field
                      id="firstName"
                      name="firstName"
                      component={FormTextField}
                      required
                      fullWidth
                      label="First Name"
                      validate={[requiredValidator]}
                      autoFocus={!enableSearch}
                    />
                  </Grid>
                  <Grid item md>
                    <Field
                      id="lastName"
                      name="lastName"
                      component={FormTextField}
                      required
                      fullWidth
                      label="Last name"
                      validate={[requiredValidator]}
                    />
                  </Grid>
                  <Grid item md>
                    <Field
                      id="email"
                      name="email"
                      type="email"
                      component={FormTextField}
                      required
                      fullWidth
                      label="Email"
                      validate={[requiredValidator, isEmailValidator]}
                    />
                  </Grid>
                </Grid>
                <Button
                  disabled={invalid}
                  style={{
                    marginTop: 10,
                    marginLeft: 'auto',
                    display: 'block',
                  }}
                  type="submit"
                  color="primary"
                >
                  Add
                </Button>
              </form>
            </div>
          )}

          {users.length > 0 && (
            <div>
              <ul className="added-list">
                {users.map((attendee, index) => renderUser(attendee, index))}
              </ul>
            </div>
          )}

          <RoleSelector
            deed={deed}
            values={this.state.roles}
            onChange={(v) => this.setState({ roles: v })}
            allowMultiple={allowMultipleRoles}
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={this.reset}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => {
              this.add();
            }}
            type="submit"
            disabled={
              (addedAttendeesWithEmail.length === 0 && addedAttendeesWithId.length === 0) ||
              (deed.roles?.size > 0 && !this.state.roles?.length)
            }
          >
            {buttonAddText}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

DeedAttendeeAddList.propTypes = {
  resetForm: PropTypes.func,
  onAdd: PropTypes.func,
  close: PropTypes.func,
  titleText: PropTypes.string,
  buttonAddText: PropTypes.string,
  handleSubmit: PropTypes.func,
  visible: PropTypes.bool,
  invalid: PropTypes.bool,
  term: PropTypes.string,
  results: PropTypes.object,
  searchLoading: PropTypes.bool,
  enableSearch: PropTypes.bool,
  deed: PropTypes.object,
  actions: PropTypes.object,
  allowMultipleRoles: PropTypes.bool,
};

export default connect(
  () => ({}),
  (dispatch) => ({
    resetForm: () => {
      dispatch(reset('AttendeesOverlayForm'));
    },
  })
)(
  reduxForm({
    form: 'AttendeesOverlayForm',
    destroyOnUnmount: false,
    enableReinitialize: true,
  })(PureRender(DeedAttendeeAddList))
);
